
import React, { useState, useRef, useEffect, useCallback } from "react";
import { BrowserMultiFormatReader } from "@zxing/library";
import "../../styles/qr-code-scanner/qr-code-scanner.scss";
import { useNavigate, Link } from "react-router-dom";
import api from "../../middleware/api";
import icon from "../../assets/problem.gif";
import InvalidQrCode from "../invalid-qr-code/invalid-qr-code";
import * as Sentry from '@sentry/react';
import { diagnosticTags } from '../../utils/tracker-diagnostics';

function QrCodeScanner() {
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [showFormAnimation, setShowFormAnimation] = useState(false);
  const [cameraAccessDenied, setCameraAccessDenied] = useState(false);

  const videoRef = useRef(null);
  const scanTimerRef = useRef(null);
  const scanAttemptRef = useRef(null);
  const retryTimerRef = useRef(null);
  const codeReaderRef = useRef(new BrowserMultiFormatReader());
  const navigate = useNavigate();

  const verifyQrCode = useCallback(async (code) => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/qrcode/details/${code}`, {});
      const qrData = response?.data?.qrCode;
      setUserData(qrData);
      if (qrData && !qrData.user_id) {
        // handle new QR code
        Sentry.captureMessage('QR scan outcome', {
          level: 'info', tags: { ...diagnosticTags(response), operation: 'qr.scan', outcome: 'unregistered' },
        });
      } else {
        // handle existing QR code
        Sentry.captureMessage('Scanned QR code is already registered', {
          level: 'info', tags: { ...diagnosticTags(response), operation: 'qr.scan', outcome: 'already_registered' },
        });
      }
    } catch (err) {
      // A registered tracker owned by another account is now forbidden (403).
      // Surface the existing "Already Registered" screen without exposing any
      // owner data or inventing an owner id.
      if (err.response?.status === 403) {
        setRegistered(true);
      } else {
        setError(err.response?.data?.resultMessage?.en)
      }
    }
  }, []);

  const startDecoding = useCallback(async () => {
    clearTimeout(scanTimerRef.current);
    const attempt = { pending: true };
    scanAttemptRef.current = attempt;
    scanTimerRef.current = setTimeout(() => {
      if (!attempt.pending || scanAttemptRef.current !== attempt) return;
      attempt.pending = false;
      Sentry.captureMessage('QR scan outcome', {
        level: 'info', tags: { ...diagnosticTags(), operation: 'qr.scan', outcome: 'no_decode_after_15s' },
      });
    }, 15000);
    try {
      await codeReaderRef.current.decodeFromVideoDevice(undefined, videoRef.current, (result, err) => {
        if (result) {
          attempt.pending = false;
          clearTimeout(scanTimerRef.current);
          const code = result.getText().split("/").pop();
          setCode(code);
          setError("");
          codeReaderRef.current.reset();
          // code.length === 6 && result.getText().includes('https://qr.roamsmarttracker.com')
          if (true) {
            verifyQrCode(code);
          } else {
            setShowFormAnimation(true);
          }
        }
        // if (err && !(err instanceof NotFoundException)) {
        //   setError("Error accessing camera or scanning QR code.");
        // }
      });
    } catch (error) {
      attempt.pending = false;
      clearTimeout(scanTimerRef.current);
      Sentry.captureException(error, { tags: { ...diagnosticTags(), operation: 'qr.camera' } });
      setError("Error initializing camera.");
      setCameraAccessDenied(true); // Set flag if camera access is denied
    }
  }, [verifyQrCode]);

  useEffect(() => {
    if (!cameraAccessDenied) {
      startDecoding();
    }
    const codeReader = codeReaderRef.current;
    const reportExit = (attempt) => {
      if (!attempt?.pending || scanAttemptRef.current !== attempt) return;
      attempt.pending = false;
      clearTimeout(scanTimerRef.current);
      Sentry.captureMessage('QR scan outcome', {
        level: 'info', tags: { ...diagnosticTags(), operation: 'qr.scan', outcome: 'exited_before_decode' },
      });
    };
    const onPageHide = () => reportExit(scanAttemptRef.current);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      const attempt = scanAttemptRef.current;
      // StrictMode immediately restarts this effect; report only a real exit.
      queueMicrotask(() => reportExit(attempt));
      clearTimeout(scanTimerRef.current);
      clearTimeout(retryTimerRef.current);
      if (codeReader) {
        codeReader.reset();
      }
    };
  }, [cameraAccessDenied, startDecoding]);

  const resetScanner = () => {
    setCode("");
    setError("");
    setUserData(null);
    setRegistered(false);
    setShowFormAnimation(false);
    setCameraAccessDenied(false);
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
    }
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      startDecoding();
    }, 500);
  };

  const heading = userData?.user_id || registered
    ? "This QR code is Already Registered"
    : "Something has gone wrong, don't worry. Let's try again.";

  const handleNextClick = () => {
    if (code) {
      navigate(`/add-qr-code/${code}`);
    }
  };

  return (
    <>
      {showFormAnimation || userData?.user_id || registered ? (
        <InvalidQrCode
          icon={icon}
          heading={heading}
          showQrCodeIcon={true}
          userId={userData?.user_id}
          onClose={resetScanner}
        />
      ) : (
        <div className="qr-scanner form-container">
          {!code && (
            <div className="header-title">
              <h1>Activate QR</h1>
              <Link to="/" className="close-menu">
                X
              </Link>
            </div>
          )}
          {!code ? (
            <div>
              {!cameraAccessDenied ? ( 
                <div className="video-container">
              <video ref={videoRef} autoPlay />
                </div>
              ) : <p style={{ color: 'rgb(250, 111, 104)' }}>
                    Please allow camera access to scan QR codes.
                  </p> }
              {!cameraAccessDenied && (
                <div style={{ marginTop: "20px" }}>
                  <p className="heading ">
                ⚠️  Scan the QR code on the back of the device in a well-lit
                  area using your phone's main (rear) camera, holding it steady about
                  6–8 inches away. If your browser has trouble detecting the code,
                   try adjusting the angle slightly or close the browser and use your 
                   phone's native camera app instead.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              {userData ? (
                userData?.user_id ? (
                  ""
                ) : (
                  <div className="qr-bg">
              {userData.image_url ? <img alt="qr-code" src={userData.image_url} /> : null}

                  </div>
                )
              ) : (
                <p style={{color:'rgb(250, 111, 104)' }}>{error}</p>
              )}
              {!userData?.user_id  && !error ? (
                <div className="button-row">
                  <button onClick={() => navigate("/")} className="cta-button cancel-btn">
                    Cancel
                  </button>
                  <button onClick={handleNextClick} className="cta-button next-btn">
                    Next
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </>
  );
}

export default QrCodeScanner;
